import { TranslateService } from '@ngx-translate/core';
import { NGXLogger } from 'ngx-logger';
import { Injectable } from '@angular/core';
import * as Stomp from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { MessengerService, MessageSubject } from './messenger.service';
import { AppConfiguration, AppConfigService } from './app-config.service';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class WebSocketService
{
  private connected = false;
  private stompService = null;
  private appConfig: AppConfiguration;

  constructor(
    private messengerService: MessengerService,
    private appConfigService: AppConfigService,
    private translate: TranslateService,
    private logger: NGXLogger,
    private toastrService: ToastrService,
    private router: Router)
  {
    this.appConfig = this.appConfigService.getConfig();
  }

  /**
   * Connect WebSocket using current token.
   *
   * @param token Logged in user token. Sent as argument to avoid circular reference.
   */
  public connect(token: string): void
  {
    // If already connected, nothing to do
    if (this.stompService === null)
    {
      // Connect WebSocket
      const url = `${this.appConfig.ws.baseUrl}?access_token=${token}`;
      this.stompService = Stomp.Stomp.over(() => new SockJS(url));
      this.stompService.connect({}, () =>
      {
        // Subscriptions
        this.wsSubscribe('/user/queue/chat/count', 'wsChatCount');
        this.wsSubscribe('/user/queue/chat/typing', 'wsChatTyping');
        this.wsSubscribe('/user/queue/users/presence', 'wsUsersPresence');
        this.wsSubscribe('/user/queue/messages/count', 'wsMessagesCount');
        this.wsSubscribe('/user/queue/settings/updated', 'wsSettingsUpdated');
        this.wsSubscribe('/user/queue/notices/updated', 'wsNoticesUpdated');
        this.wsSubscribe('/user/queue/user/notice/received', 'wsUserNoticedReceived');
        this.wsSubscribe('/user/queue/document/query/status/changed', 'wsDocumentQueryStatusChanged');
        this.connected = true;
      });
    }
  }

  /**
   * Disconnect WebSocket connection.
   */
  public disconnect(): void
  {
    if (this.connected)
    {
      if (this.stompService !== null)  // Should not be null but check for security
      {
        this.stompService.disconnect();
      }
      this.stompService = null;
      this.connected = false;
    }
  }

  /**
   * This method navigate to a specific Member Id
   *
   * @param memberId Member Id to navigate to.
   */
  public openMember(memberId: number): void
  {
    const now = new Date().getTime();
    this.router.navigateByUrl(`/patient/${memberId}?tab=clinical-records&ts=${now}`);
  }

  /**
   * Subscribe to Web Socket url.
   *
   * @param url Web Socket url
   * @param messageSubject Message to process sent to messenger service
   */
  private wsSubscribe(url: string, messageSubject: string): void
  {
    this.stompService.subscribe(url, (message: any) =>
    {
      this.logger.info('WebSocket message received!', message);
      this.messengerService.sendMessage(new MessageSubject(messageSubject, message));

      // For document query status, send user feedback of completed query
      if (messageSubject === 'wsDocumentQueryStatusChanged')
      {
        this.translate.get(['CLINICAL-RECORD-COMPLETED-SUCCESSFULLY', 'CLINICAL-RECORD-COMPLETED-WITH-ERROR']).subscribe({
          next: (res) =>
          {
            const responseMessage = JSON.parse(message.body);

            // Use injector to workaround "Cannot instantiate cyclic dependency!" error
            const isCompletedOK = responseMessage.status === 'COMPLETED';
            let messageText = isCompletedOK ?
              res['CLINICAL-RECORD-COMPLETED-SUCCESSFULLY'] :
              res['CLINICAL-RECORD-COMPLETED-WITH-ERROR'];

            // Refresh Patient Summary tab (if opened)
            this.messengerService.sendMessage(new MessageSubject('refreshSummaryCounts'));

            switch (responseMessage.status)
            {
              case 'COMPLETED':
              case 'COMPLETED_IN_ERROR':
                messageText = messageText.replace('{{patientFullName}}', responseMessage.member.name.fullName);

                // Display success or error
                this.toastrService[isCompletedOK ? 'success' : 'error'](messageText, null,
                  {
                    enableHtml: true,
                    timeOut: isCompletedOK ? 0 : 5000
                  })
                  .onTap.subscribe({
                    next: () =>
                    {
                      this.openMember(responseMessage.member.id);
                    }
                  });
                break;
            }
          }
        });
      }
    });
  }
}
